
// src/components/profile/ProfileSettings.tsx
import { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next'; 
import { Camera, Bell, Shield, Trash2, CheckCircle, User, Loader2 } from 'lucide-react';
import { toast } from "sonner";
import {
  getCustomerProfile,
  updateCustomerProfile,
  uploadCustomerAvatar,
  getNotificationPreferences,
  updateNotificationPreferences,
  deleteCustomerAccount,
} from "../../../services/customerprofile.service";
import { getCountryDropdown } from "../../../services/country.service";
import { getStateDropdown } from "../../../services/state.service";
import { getCityDropdown } from "../../../services/city.service";
import { Seo } from "../../components/Seo";

const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
);

function ProfileSettingsSkeleton() {
  return (
    <div className="p-4 md:p-8">
      <div className="mb-6">
        <Skeleton className="h-8 w-56 mb-2" />
        <Skeleton className="h-4 w-72" />
      </div>
      <div className="flex gap-2 mb-6 overflow-x-auto">
        {[1, 2, 3].map((tab) => (
          <Skeleton key={tab} className="h-10 w-28 rounded-xl flex-shrink-0" />
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
        <div className="flex items-center gap-4 mb-6 pb-6 border-b border-[#E5E7EB]">
          <Skeleton className="w-20 h-20 rounded-2xl" />
          <div>
            <Skeleton className="h-5 w-36 mb-2" />
            <Skeleton className="h-4 w-32 mb-2" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4, 5, 6].map((field) => (
            <div key={field} className={field === 5 || field === 6 ? "md:col-span-2" : ""}>
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-12 w-full rounded-xl" />
            </div>
          ))}
          <div className="md:col-span-2">
            <Skeleton className="h-4 w-20 mb-2" />
            <Skeleton className="h-24 w-full rounded-xl" />
          </div>
        </div>
        <div className="flex gap-3 mt-6 pt-6 border-t border-[#E5E7EB]">
          <Skeleton className="h-12 w-40 rounded-xl" />
          <Skeleton className="h-12 w-28 rounded-xl" />
        </div>
      </div>
    </div>
  );
}

export function ProfileSettings() {
  const { t } = useTranslation('auth'); 
  const [tab, setTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Profile data
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    countryId: '',
    stateId: '',
    cityId: '',
    bio: '',
    profilePhoto: '',
    customerType: 1,
  });

  // Dropdown options
  const [countries, setCountries] = useState<{ ID: string; CountryName: string }[]>([]);
  const [states, setStates] = useState<{ ID: string; StateName: string }[]>([]);
  const [cities, setCities] = useState<{ ID: string; CityName: string }[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Notification preferences
  const [notifications, setNotifications] = useState({
    newLead: true,
    newMessage: true,
    listingExpiry: true,
    priceAlerts: false,
    platformUpdates: false,
    marketingEmails: false,
  });

  // Get customer type label
  const getCustomerTypeLabel = (type: number) => {
    const map: Record<number, string> = {
      1: t('profileSettings.profile.fields.customerType.buyer'),
      2: t('profileSettings.profile.fields.customerType.privateSeller'),
      3: t('profileSettings.profile.fields.customerType.company'),
    };
    return map[type] || t('profileSettings.profile.fields.customerType.buyer');
  };

  // Load countries on mount
  useEffect(() => {
    const loadCountries = async () => {
      try {
        setLoadingCountries(true);
        const data = await getCountryDropdown();
        setCountries(data || []);
      } catch (error) {
        toast.error(t('profileSettings.errors.loadCountries'));
      } finally {
        setLoadingCountries(false);
      }
    };
    loadCountries();
  }, []);

  // Load states when country changes
  useEffect(() => {
    const loadStates = async () => {
      if (!profile.countryId) {
        setStates([]);
        return;
      }
      try {
        setLoadingStates(true);
        const data = await getStateDropdown(profile.countryId);
        setStates(data || []);
      } catch (error) {
        toast.error(t('profileSettings.errors.loadStates'));
      } finally {
        setLoadingStates(false);
      }
    };
    loadStates();
  }, [profile.countryId]);

  // Load cities when state changes
  useEffect(() => {
    const loadCities = async () => {
      if (!profile.stateId) {
        setCities([]);
        return;
      }
      try {
        setLoadingCities(true);
        const data = await getCityDropdown(profile.stateId);
        setCities(data || []);
      } catch (error) {
        toast.error(t('profileSettings.errors.loadCities'));
      } finally {
        setLoadingCities(false);
      }
    };
    loadCities();
  }, [profile.stateId]);

  // Load profile and notifications
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [profileRes, notifRes] = await Promise.all([
          getCustomerProfile(),
          getNotificationPreferences(),
        ]);

        if (profileRes.success) {
          const data = profileRes.data;
          setProfile({
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: data.email || '',
            mobileNumber: data.mobileNumber || '',
            countryId: data.country || '',
            stateId: data.state || '',
            cityId: data.city || '',
            bio: data.bio || '',
            profilePhoto: data.profilePhoto || '',
            customerType: data.customerType || 1,
          });
        }

        if (notifRes.success) {
          setNotifications(notifRes.data || {});
        }
      } catch (error: any) {
        toast.error(error?.message || t('profileSettings.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Handle profile update
  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      const payload = {
        firstName: profile.firstName,
        lastName: profile.lastName,
        mobileNumber: profile.mobileNumber,
        country: profile.countryId,
        state: profile.stateId,
        city: profile.cityId,
        bio: profile.bio,
      };
      const res = await updateCustomerProfile(payload);
      if (res.success) {
        setSaved(true);
        toast.success(t('profileSettings.profile.buttons.success'));
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (error: any) {
      toast.error(error?.message || t('profileSettings.profile.buttons.failed'));
    } finally {
      setSaving(false);
    }
  };

  // Handle avatar upload
  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('profileSettings.profile.avatar.errorSize'));
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error(t('profileSettings.profile.avatar.errorType'));
      return;
    }

    const formData = new FormData();
    formData.append('avatar', file);

    try {
      setUploading(true);
      const res = await uploadCustomerAvatar(formData);
      if (res.success && res.data?.avatar) {
        setProfile(prev => ({ ...prev, profilePhoto: res.data.avatar }));
        toast.success(t('profileSettings.profile.avatar.success'));
      }
    } catch (error: any) {
      toast.error(error?.message || t('profileSettings.profile.avatar.failed'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Handle notification toggle
  const handleNotificationToggle = async (key: keyof typeof notifications) => {
    const updated = { ...notifications, [key]: !notifications[key] };
    setNotifications(updated);
    try {
      await updateNotificationPreferences(updated);
      toast.success(t('profileSettings.notifications.success'));
    } catch (error: any) {
      toast.error(error?.message || t('profileSettings.notifications.failed'));
      setNotifications(notifications);
    }
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    if (!confirm(t('profileSettings.security.deleteAccount.confirm'))) return;
    try {
      const res = await deleteCustomerAccount();
      if (res.success) {
        toast.success(t('profileSettings.security.deleteAccount.success'));
        window.location.href = '/';
      }
    } catch (error: any) {
      toast.error(error?.message || t('profileSettings.security.deleteAccount.failed'));
    }
  };

  if (loading) {
    return <ProfileSettingsSkeleton />;
  }

  // Tab configuration with translations
  const tabs = [
    { id: 'profile', label: t('profileSettings.tabs.profile'), icon: '👤' },
    { id: 'notifications', label: t('profileSettings.tabs.notifications'), icon: '🔔' },
    { id: 'security', label: t('profileSettings.tabs.security'), icon: '🛡️' },
  ];

  // Notification items with translations
  const notificationItems = [
    { key: 'newLead', label: t('profileSettings.notifications.items.newLead.label'), desc: t('profileSettings.notifications.items.newLead.description') },
    { key: 'newMessage', label: t('profileSettings.notifications.items.newMessage.label'), desc: t('profileSettings.notifications.items.newMessage.description') },
    { key: 'listingExpiry', label: t('profileSettings.notifications.items.listingExpiry.label'), desc: t('profileSettings.notifications.items.listingExpiry.description') },
    { key: 'priceAlerts', label: t('profileSettings.notifications.items.priceAlerts.label'), desc: t('profileSettings.notifications.items.priceAlerts.description') },
    { key: 'platformUpdates', label: t('profileSettings.notifications.items.platformUpdates.label'), desc: t('profileSettings.notifications.items.platformUpdates.description') },
    { key: 'marketingEmails', label: t('profileSettings.notifications.items.marketingEmails.label'), desc: t('profileSettings.notifications.items.marketingEmails.description') },
  ];

  return (
    <>
    <Seo
      title="Profile Settings | Morrocars"
      description="Manage your Morrocars profile, personal information, notification preferences and account security settings."
    />
    <div className="p-4 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl text-[#2D323A]">{t('profileSettings.title')}</h1>
        <p className="text-[#717182]">{t('profileSettings.subtitle')}</p>
      </div>

      {saved && (
        <div className="bg-[#22C55E]/10 border border-[#22C55E]/20 rounded-xl p-3 flex items-center gap-2 mb-5 text-[#22C55E]">
          <CheckCircle className="w-5 h-5" />
          <span className="text-sm">{t('profileSettings.saved')}</span>
        </div>
      )}

      <div className="flex gap-2 mb-6 overflow-x-auto">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm whitespace-nowrap transition-colors ${
              tab === t.id
                ? 'bg-[#1E6DFF] text-white'
                : 'bg-white border border-[#E5E7EB] text-[#2D323A] hover:border-[#1E6DFF]'
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
        {tab === 'profile' && (
          <div>
            {/* Avatar */}
            <div className="flex items-center gap-4 mb-6 pb-6 border-b border-[#E5E7EB]">
              <div className="relative">
                <div className="w-20 h-20 rounded-2xl bg-[#1E6DFF] flex items-center justify-center text-white text-2xl overflow-hidden">
                  {profile.profilePhoto ? (
                    <img
                      src={profile.profilePhoto}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-10 h-10" />
                  )}
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="absolute -bottom-1 -right-1 p-1.5 bg-white border border-[#E5E7EB] rounded-xl shadow hover:bg-[#F5F7FA] transition-colors disabled:opacity-50"
                >
                  {uploading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-[#1E6DFF]" />
                  ) : (
                    <Camera className="w-3.5 h-3.5 text-[#717182]" />
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
              </div>
              <div>
                <p className="text-[#2D323A]">
                  {profile.firstName} {profile.lastName}
                </p>
                <p className="text-sm text-[#717182]">
                  {getCustomerTypeLabel(profile.customerType)}
                </p>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm text-[#1E6DFF] hover:underline mt-1"
                >
                  {t('profileSettings.profile.avatar.changePhoto')}
                </button>
              </div>
            </div>

            {/* Profile Form */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#2D323A] mb-1.5">{t('profileSettings.profile.fields.firstName')}</label>
                <input
                  type="text"
                  value={profile.firstName}
                  onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                  placeholder={t('profileSettings.profile.fields.firstNamePlaceholder')}
                  className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm text-[#2D323A] mb-1.5">{t('profileSettings.profile.fields.lastName')}</label>
                <input
                  type="text"
                  value={profile.lastName}
                  onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                  placeholder={t('profileSettings.profile.fields.lastNamePlaceholder')}
                  className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm text-[#2D323A] mb-1.5">{t('profileSettings.profile.fields.email')}</label>
                <input
                  type="email"
                  value={profile.email}
                  disabled
                  className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl opacity-70 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm text-[#2D323A] mb-1.5">{t('profileSettings.profile.fields.phone')}</label>
                <input
                  type="tel"
                  value={profile.mobileNumber}
                  onChange={(e) => setProfile({ ...profile, mobileNumber: e.target.value })}
                  className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] transition-colors"
                />
              </div>

              {/* Country Dropdown */}
              <div>
                <label className="block text-sm text-[#2D323A] mb-1.5">{t('profileSettings.profile.fields.country')}</label>
                <select
                  value={profile.countryId}
                  onChange={(e) => setProfile({ ...profile, countryId: e.target.value, stateId: '', cityId: '' })}
                  disabled={loadingCountries}
                  className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] transition-colors"
                >
                  <option value="">{t('profileSettings.profile.fields.countryPlaceholder')}</option>
                  {countries.map(c => <option key={c.ID} value={c.ID}>{c.CountryName}</option>)}
                </select>
              </div>

              {/* State Dropdown */}
              <div>
                <label className="block text-sm text-[#2D323A] mb-1.5">{t('profileSettings.profile.fields.state')}</label>
                <select
                  value={profile.stateId}
                  onChange={(e) => setProfile({ ...profile, stateId: e.target.value, cityId: '' })}
                  disabled={!profile.countryId || loadingStates}
                  className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] transition-colors"
                >
                  <option value="">
                    {profile.countryId ? t('profileSettings.profile.fields.statePlaceholder') : t('profileSettings.profile.fields.stateDisabled')}
                  </option>
                  {states.map(s => <option key={s.ID} value={s.ID}>{s.StateName}</option>)}
                </select>
              </div>

              {/* City Dropdown */}
              <div className="md:col-span-2">
                <label className="block text-sm text-[#2D323A] mb-1.5">{t('profileSettings.profile.fields.city')}</label>
                <select
                  value={profile.cityId}
                  onChange={(e) => setProfile({ ...profile, cityId: e.target.value })}
                  disabled={!profile.stateId || loadingCities}
                  className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] transition-colors"
                >
                  <option value="">
                    {profile.stateId ? t('profileSettings.profile.fields.cityPlaceholder') : t('profileSettings.profile.fields.cityDisabled')}
                  </option>
                  {cities.map(c => <option key={c.ID} value={c.ID}>{c.CityName}</option>)}
                </select>
              </div>

              {/* Bio */}
              <div className="md:col-span-2">
                <label className="block text-sm text-[#2D323A] mb-1.5">{t('profileSettings.profile.fields.bio')}</label>
                <textarea
                  rows={3}
                  value={profile.bio}
                  onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                  placeholder={t('profileSettings.profile.fields.bioPlaceholder')}
                  className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] resize-none"
                />
              </div>
            </div>
          </div>
        )}

        {tab === 'notifications' && (
          <div className="space-y-4">
            <h3 className="text-[#2D323A] mb-4">{t('profileSettings.notifications.title')}</h3>
            {notificationItems.map(n => (
              <div key={n.key} className="flex items-center justify-between p-4 bg-[#F5F7FA] rounded-xl">
                <div>
                  <p className="text-sm text-[#2D323A]">{n.label}</p>
                  <p className="text-xs text-[#717182]">{n.desc}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifications[n.key as keyof typeof notifications] || false}
                    onChange={() => handleNotificationToggle(n.key as keyof typeof notifications)}
                    className="sr-only peer"
                  />
                  <div className="w-10 h-6 bg-[#E5E7EB] peer-checked:bg-[#1E6DFF] rounded-full transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-4" />
                </label>
              </div>
            ))}
          </div>
        )}

        {tab === 'security' && (
          <div>
            <h3 className="text-[#2D323A] mb-4">{t('profileSettings.security.title')}</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-[#F5F7FA] rounded-xl">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-[#22C55E]" />
                  <div>
                    <p className="text-sm text-[#2D323A]">{t('profileSettings.security.twoFactor.title')}</p>
                    <p className="text-xs text-[#717182]">{t('profileSettings.security.twoFactor.description')}</p>
                  </div>
                </div>
                <button className="px-4 py-2 bg-[#1E6DFF] text-white rounded-xl text-sm hover:bg-[#1557d9] transition-colors">
                  {t('profileSettings.security.twoFactor.button')}
                </button>
              </div>
              <div className="border border-[#EF4444]/20 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Trash2 className="w-5 h-5 text-[#EF4444]" />
                  <p className="text-sm text-[#EF4444]">{t('profileSettings.security.deleteAccount.title')}</p>
                </div>
                <p className="text-xs text-[#717182] mb-3">
                  {t('profileSettings.security.deleteAccount.description')}
                </p>
                <button
                  onClick={handleDeleteAccount}
                  className="px-4 py-2 border border-[#EF4444] text-[#EF4444] rounded-xl text-sm hover:bg-[#EF4444] hover:text-white transition-colors"
                >
                  {t('profileSettings.security.deleteAccount.button')}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-3 mt-6 pt-6 border-t border-[#E5E7EB]">
          <button
            onClick={handleSaveProfile}
            disabled={saving}
            className="px-8 py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? t('profileSettings.profile.buttons.saving') : t('profileSettings.profile.buttons.save')}
          </button>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 border border-[#E5E7EB] text-[#717182] rounded-xl hover:bg-[#F5F7FA] transition-colors"
          >
            {t('profileSettings.profile.buttons.cancel')}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}